import React from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  Stack,
  Button,
  Tooltip,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  RestoreOutlined as ResetIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { OciAiTestDto } from '../../dto/OciAiTestDto';

interface AdvancedOptionsProps {
  options: OciAiTestDto.AdvancedOptions;
  onChange: (options: OciAiTestDto.AdvancedOptions) => void;
}

/**
 * AI 모델 고급 옵션 설정 컴포넌트
 * 
 * @description Temperature, Max Tokens 등 AI 모델의 고급 파라미터 조정
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */
export const AdvancedOptions: React.FC<AdvancedOptionsProps> = ({
  options,
  onChange,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  // 기본값
  const defaultOptions: OciAiTestDto.AdvancedOptions = {
    temperature: 0.7,
    max_tokens: 2000,
    top_p: 0.95,
    top_k: 40,
    frequency_penalty: 0,
    presence_penalty: 0,
  };

  // 옵션 업데이트
  const updateOption = (key: keyof OciAiTestDto.AdvancedOptions, value: number) => {
    onChange({
      ...options,
      [key]: value,
    });
  };

  // 기본값으로 리셋
  const resetToDefaults = () => {
    onChange(defaultOptions);
  };

  // 툴팁 텍스트
  const tooltips = {
    temperature: 'Controls randomness (0.0 = deterministic, 2.0 = very random)',
    max_tokens: 'Maximum number of tokens to generate',
    top_p: 'Nucleus sampling: considers tokens with cumulative probability up to P',
    top_k: 'Consider only the K most likely next tokens',
    frequency_penalty: 'Reduces repetition based on token frequency (-2.0 to 2.0)',
    presence_penalty: 'Reduces repetition based on token presence (-2.0 to 2.0)',
  };

  return (
    <Box>
      {/* 기본 옵션 (항상 표시) */}
      <Stack spacing={3}>
        {/* Temperature */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              Temperature
            </Typography>
            <Tooltip title={tooltips.temperature} placement="top">
              <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
            <Typography variant="caption" color="primary.main" fontWeight={500}>
              {options.temperature}
            </Typography>
          </Stack>
          <Slider
            value={options.temperature}
            onChange={(_, value) => updateOption('temperature', value as number)}
            min={0}
            max={2}
            step={0.1}
            marks={[
              { value: 0, label: '0' },
              { value: 1, label: '1' },
              { value: 2, label: '2' },
            ]}
            size="small"
          />
        </Box>

        {/* Max Tokens */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              Max Tokens
            </Typography>
            <Tooltip title={tooltips.max_tokens} placement="top">
              <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
          </Stack>
          <TextField
            type="number"
            value={options.max_tokens}
            onChange={(e) => updateOption('max_tokens', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 4000 }}
          />
        </Box>
      </Stack>

      {/* 고급 옵션 토글 */}
      <Box sx={{ mt: 2 }}>
        <Button
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          size="small"
          variant="text"
          fullWidth
          sx={{ justifyContent: 'space-between' }}
        >
          고급 옵션
        </Button>

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={3}>
            {/* Top P */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  Top P
                </Typography>
                <Tooltip title={tooltips.top_p} placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
                <Typography variant="caption" color="primary.main" fontWeight={500}>
                  {options.top_p}
                </Typography>
              </Stack>
              <Slider
                value={options.top_p}
                onChange={(_, value) => updateOption('top_p', value as number)}
                min={0}
                max={1}
                step={0.05}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' },
                ]}
                size="small"
              />
            </Box>

            {/* Top K */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  Top K
                </Typography>
                <Tooltip title={tooltips.top_k} placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                value={options.top_k}
                onChange={(e) => updateOption('top_k', parseInt(e.target.value) || 0)}
                size="small"
                fullWidth
                inputProps={{ min: 1, max: 100 }}
              />
            </Box>

            {/* Frequency Penalty */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  Frequency Penalty
                </Typography>
                <Tooltip title={tooltips.frequency_penalty} placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
                <Typography variant="caption" color="primary.main" fontWeight={500}>
                  {options.frequency_penalty}
                </Typography>
              </Stack>
              <Slider
                value={options.frequency_penalty}
                onChange={(_, value) => updateOption('frequency_penalty', value as number)}
                min={-2}
                max={2}
                step={0.1}
                marks={[
                  { value: -2, label: '-2' },
                  { value: 0, label: '0' },
                  { value: 2, label: '2' },
                ]}
                size="small"
              />
            </Box>

            {/* Presence Penalty */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  Presence Penalty
                </Typography>
                <Tooltip title={tooltips.presence_penalty} placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
                <Typography variant="caption" color="primary.main" fontWeight={500}>
                  {options.presence_penalty}
                </Typography>
              </Stack>
              <Slider
                value={options.presence_penalty}
                onChange={(_, value) => updateOption('presence_penalty', value as number)}
                min={-2}
                max={2}
                step={0.1}
                marks={[
                  { value: -2, label: '-2' },
                  { value: 0, label: '0' },
                  { value: 2, label: '2' },
                ]}
                size="small"
              />
            </Box>

            {/* 리셋 버튼 */}
            <Button
              onClick={resetToDefaults}
              startIcon={<ResetIcon />}
              variant="outlined"
              size="small"
              fullWidth
            >
              기본값으로 리셋
            </Button>
          </Stack>
        </Collapse>
      </Box>
    </Box>
  );
};